import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { AuthCard, AuthCardLinks, AuthErrorSummary } from './AuthCard';
import { AuthField } from './AuthField';
import { SubmitButton } from './SubmitButton';
import { loginSchema, type LoginValues } from './schemas';
import { useServerValidation } from './useServerValidation';

const FIELDS = ['email', 'password'] as const;

/**
 * Accepts a site-relative path only. `//host` and `https://host` are rejected:
 * the return URL reaches this screen from a query parameter, and following one
 * off-site would turn the login page into an open redirect. This is the client
 * half of what `Url.IsLocalUrl` did for the Razor page.
 */
function localPath(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

/**
 * Replaces `Pages/Auth/Login.cshtml`.
 *
 * The outcome mapping is `LoginModel.OnPostAsync`, moved to the client: the
 * endpoint answers 200 with flags rather than a status code, because a refused
 * password is a normal answer and a 401 here would be indistinguishable from an
 * expired session.
 *
 * Sign-in never lands on the login page itself, and there are two ways it can
 * be told where to go. `RequireAuth` puts the blocked location in
 * `location.state.from` — the SPA's own mechanism. A `returnUrl` query is the
 * other: the cookie middleware's `LoginPath` appends one when an anonymous
 * request hits a page that is still Razor. Router state wins, because only a
 * real in-app navigation sets it. `replace` keeps the form out of the history
 * either way.
 *
 * There is no sign-up link: accounts are created by an administrator.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();

  const state = location.state as { from?: { pathname?: string }; status?: string } | null;
  // ASP.NET spells it ReturnUrl; the hand-written redirects use returnUrl.
  const returnUrl = localPath(searchParams.get('ReturnUrl') ?? searchParams.get('returnUrl'));
  const from = state?.from?.pathname ?? returnUrl ?? '/';

  // The Razor page's TempData["StatusMessage"], carried in router state instead:
  // the reset and activation screens both end here with a confirmation.
  const [status, setStatus] = useState(state?.status ?? null);

  const schema = useMemo(() => loginSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const { formErrors, clearFormErrors, showFormError, applyServerErrors } = useServerValidation(setError, FIELDS);

  const onSubmit = handleSubmit(async (values) => {
    clearFormErrors();

    try {
      // rememberMe is sent false: the Razor form never rendered the checkbox its
      // input model supports, so persistent cookies were never actually offered.
      const result = await login({ ...values, rememberMe: false });

      if (result.succeeded) {
        // RequireAuth redirects to the change-password screen on its own once
        // /me reports the flag, so success only has to decide where to land.
        navigate(result.mustChangePassword ? '/auth-changepassword' : from, { replace: true });
        return;
      }

      if (result.isLockedOut) {
        navigate('/auth-lockout', { replace: true });
        return;
      }

      if (result.requiresTwoFactor) {
        // The password was accepted and Identity issued its two-factor cookie;
        // the challenge screen reads the rest from that. `from` travels in router
        // state so the user still lands where they were headed after the code.
        navigate('/auth-mfa-challenge', { replace: true, state: { from } });
        return;
      }

      showFormError(result.error ?? t('auth:Auth.Error.InvalidCredentials', 'Invalid email or password'));
    } catch (error) {
      applyServerErrors(error);
    }
  });

  return (
    <AuthCard
      title={t('auth:Auth.WelcomeBack', 'Welcome Back')}
      subtitle={t('auth:Auth.SignInToContinue', 'Sign in to continue to your account')}
      footer={<AuthCardLinks />}
    >
      {status && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {status}
          <button
            type="button"
            className="btn-close"
            aria-label={t('Close', 'Close')}
            onClick={() => setStatus(null)}
          />
        </div>
      )}

      <AuthErrorSummary errors={formErrors} />

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          {...register('email')}
          label={t('auth:Auth.Email', 'Email Address')}
          type="email"
          large
          autoComplete="username"
          autoFocus
          placeholder={t('auth:Auth.EnterEmail', 'Enter your email')}
          error={errors.email?.message}
        />

        <AuthField
          {...register('password')}
          label={t('auth:Auth.Password', 'Password')}
          labelAside={
            <Link to="/auth-forgotpassword" className="small text-decoration-none">
              {t('auth:Auth.ForgotPassword', 'Forgot Password?')}
            </Link>
          }
          type="password"
          large
          autoComplete="current-password"
          placeholder={t('auth:Auth.EnterPassword', 'Enter your password')}
          error={errors.password?.message}
        />

        <div className="d-grid gap-2">
          <SubmitButton icon="log-in" label={t('auth:Auth.SignIn', 'Sign In')} pending={isSubmitting} />
        </div>
      </form>

    </AuthCard>
  );
}
